import {
  VegaButton,
  VegaCodeBlock,
  VegaFlex,
  VegaFont,
  VegaIcon,
  VegaInput,
  VegaPageNotification,
} from '@globalpayments/vega-react';
import DetailsModal from '../../../components/detailsModal';
import { maximumDetailsRequestReasonLength, sanitizeDetailsRequestReason } from '../../../api/kirbyFiltering';
import { KirbyDetailsIdentifier } from '../../../types/kirbyFiltering';
import { formatCodeBlock } from '../../../utility/formatCodeBlock';
import styles from '../index.module.css';

interface FilterDetailsModalProps {
  details?: unknown;
  filterType: string;
  isError: boolean;
  isLoading: boolean;
  onClose: () => void;
  onRequestDetails: (requestReason: string) => void;
  open: boolean;
  recordId?: KirbyDetailsIdentifier;
  requestReason: string;
  setRequestReason: (value: string) => void;
}

const minimumReasonLength = 30;

/** Collects the required audit reason and displays raw details for any Kirby result category. */
const FilterDetailsModal = ({
  details,
  filterType,
  isError,
  isLoading,
  onClose,
  onRequestDetails,
  open,
  recordId,
  requestReason,
  setRequestReason,
}: FilterDetailsModalProps) => {
  const normalizedReason = sanitizeDetailsRequestReason(requestReason).trim();
  const canRequestDetails =
    normalizedReason.length >= minimumReasonLength &&
    normalizedReason.length <= maximumDetailsRequestReasonLength &&
    !isLoading;
  const isShowingResponse = details !== undefined || isError;

  return (
    <DetailsModal
      backdrop="static"
      className={styles.detailsModal}
      footerActions={
        !isShowingResponse && (
          <VegaButton
            disabled={!canRequestDetails}
            label={isLoading ? 'Loading details' : 'View details'}
            onVegaClick={() => onRequestDetails(normalizedReason)}
          />
        )
      }
      modalTitle={isShowingResponse ? `${filterType} details` : `View ${filterType} details`}
      onClose={onClose}
      open={open}
      width={{ default: '90%', M: '70%' }}>
      {isShowingResponse ? (
        isError ? (
          <VegaPageNotification
            duration={0}
            message="Kirby could not retrieve details for this record. Try again from the results table."
            notificationTitle="Details unavailable"
            type="error"
          />
        ) : (
          <VegaCodeBlock readOnly showCopyButton value={formatCodeBlock(details)} />
        )
      ) : (
        <>
          <VegaFlex alignItems="center" gap="size-8">
            <VegaIcon
              aria-label="Details access is logged"
              color="text-secondary"
              icon="fa-solid fa-lock"
              size="size-16"
            />
            <VegaFont as="p" color="text-secondary" variant="font-p2-short">
              Provide a reason of at least {minimumReasonLength} characters before viewing record {recordId}.
            </VegaFont>
          </VegaFlex>
          <VegaInput
            aria-label="Request reason"
            autoValidation={false}
            isValid={
              requestReason.length === 0
                ? undefined
                : normalizedReason.length >= minimumReasonLength &&
                  normalizedReason.length <= maximumDetailsRequestReasonLength
            }
            label="Request reason"
            minLength={minimumReasonLength}
            maxLength={maximumDetailsRequestReasonLength}
            onVegaChange={(event) => setRequestReason(sanitizeDetailsRequestReason(event.detail))}
            placeholder="Explain why these record details are needed"
            required
            value={requestReason}
          />
        </>
      )}
    </DetailsModal>
  );
};

export default FilterDetailsModal;
